/**
 * ReferenceTab — markdown viewer for PDO reference documentation.
 *
 * Left: document selector + auto-generated TOC.
 * Right: ReactMarkdown rendered content with concept cross-linking.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText, ChevronRight, Loader2, Hash } from 'lucide-react';
import { clsx } from 'clsx';
import { useOntologyStore } from '@/stores/ontology';
import { ONTOLOGY_REFERENCE_DOCS } from '@/services/api';

type DocId = typeof ONTOLOGY_REFERENCE_DOCS[number]['id'];

interface TocItem {
  level: number;
  text: string;
  anchor: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function buildToc(markdown: string): TocItem[] {
  const lines = markdown.split('\n');
  const items: TocItem[] = [];
  for (const line of lines) {
    const match = line.match(/^(#{1,4})\s+(.+)/);
    if (match) {
      const level = match[1].length;
      const text  = match[2].trim();
      items.push({ level, text, anchor: slugify(text) });
    }
  }
  return items;
}

export function ReferenceTab() {
  const [activeDoc, setActiveDoc] = useState<DocId>('knowledge-garden');
  const [tocVisible, setTocVisible] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  const referenceDoc      = useOntologyStore((s) => s.referenceDoc);
  const referenceLoading  = useOntologyStore((s) => s.referenceLoading);
  const fetchReference    = useOntologyStore((s) => s.fetchReference);
  const currentGraph      = useOntologyStore((s) => s.currentGraph());
  const setTab            = useOntologyStore((s) => s.setTab);
  const setFocusedNode    = useOntologyStore((s) => s.setFocusedNode);

  useEffect(() => {
    fetchReference(activeDoc);
  }, [activeDoc, fetchReference]);

  const toc = useMemo(
    () => (referenceDoc?.name === activeDoc ? buildToc(referenceDoc.content) : []),
    [referenceDoc, activeDoc],
  );

  // Build a set of all concept IDs for cross-linking
  const conceptIds = useMemo(
    () => new Set(currentGraph?.nodes.map((n) => n.id) ?? []),
    [currentGraph],
  );

  const handleTocClick = useCallback((anchor: string) => {
    if (!contentRef.current) return;
    const el = contentRef.current.querySelector(`[id="${anchor}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Cross-link: when content includes a known concept ID, turn it into a link
  // We do this in a custom component for inline code spans
  const components = useMemo(() => ({
    // Custom heading renderer to add anchor IDs
    h1: ({ children, ...props }: React.ComponentProps<'h1'>) => {
      const text = String(children ?? '');
      const id   = slugify(text);
      return <h1 id={id} {...props}>{children}</h1>;
    },
    h2: ({ children, ...props }: React.ComponentProps<'h2'>) => {
      const text = String(children ?? '');
      const id   = slugify(text);
      return <h2 id={id} {...props}>{children}</h2>;
    },
    h3: ({ children, ...props }: React.ComponentProps<'h3'>) => {
      const text = String(children ?? '');
      const id   = slugify(text);
      return <h3 id={id} {...props}>{children}</h3>;
    },
    h4: ({ children, ...props }: React.ComponentProps<'h4'>) => {
      const text = String(children ?? '');
      const id   = slugify(text);
      return <h4 id={id} {...props}>{children}</h4>;
    },
    // Inline code: if the content matches a concept ID, cross-link it
    code: ({ children, className, ...props }: React.ComponentProps<'code'>) => {
      const content = String(children ?? '');
      // Only for inline code (no language class)
      if (!className && conceptIds.has(content)) {
        const node = currentGraph?.nodes.find((n) => n.id === content);
        return (
          <button
            onClick={() => {
              setFocusedNode(content);
              setTab('explorer');
            }}
            className="px-1 py-0.5 rounded font-mono text-[0.85em] bg-brand-teal/20 text-brand-teal border border-brand-teal/30 hover:bg-brand-teal/40 transition-colors cursor-pointer"
            title={`View in Explorer: ${node?.name ?? content}`}
          >
            {content}
          </button>
        );
      }
      return <code className={className} {...props}>{children}</code>;
    },
  }), [conceptIds, currentGraph, setFocusedNode, setTab]);

  const activeDocMeta = ONTOLOGY_REFERENCE_DOCS.find((d) => d.id === activeDoc);

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* Left: doc selector + TOC */}
      <div className="w-64 flex-shrink-0 bg-navy-900 border-r border-navy-700 flex flex-col overflow-hidden">
        {/* Document list */}
        <div className="p-3 border-b border-navy-800">
          <p className="text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider mb-2">
            Documents
          </p>
          <div className="space-y-0.5">
            {ONTOLOGY_REFERENCE_DOCS.map((doc) => (
              <button
                key={doc.id}
                onClick={() => setActiveDoc(doc.id as DocId)}
                className={clsx(
                  'w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs transition-colors text-left',
                  doc.id === activeDoc
                    ? 'bg-navy-700 text-white border border-navy-600'
                    : 'text-slate-400 hover:bg-navy-800 hover:text-white',
                )}
              >
                <FileText size={13} className="flex-shrink-0" />
                <span className="truncate">{doc.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* TOC */}
        {toc.length > 0 && (
          <div className="flex-1 overflow-y-auto p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-mono uppercase text-slate-600 font-bold tracking-wider">
                Contents
              </p>
              <button
                onClick={() => setTocVisible(!tocVisible)}
                className="text-slate-600 hover:text-white transition-colors"
              >
                <ChevronRight size={12} className={clsx('transition-transform', tocVisible ? 'rotate-90' : '')} />
              </button>
            </div>
            {tocVisible && (
              <nav className="space-y-0.5">
                {toc.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => handleTocClick(item.anchor)}
                    className={clsx(
                      'w-full text-left text-[11px] text-slate-500 hover:text-slate-200 transition-colors py-0.5 truncate flex items-center gap-1',
                      item.level === 1 && 'pl-0 font-medium',
                      item.level === 2 && 'pl-2',
                      item.level === 3 && 'pl-4 text-[10px]',
                      item.level === 4 && 'pl-6 text-[10px]',
                    )}
                    title={item.text}
                  >
                    <Hash size={9} className="flex-shrink-0 opacity-40" />
                    {item.text}
                  </button>
                ))}
              </nav>
            )}
          </div>
        )}

        {/* Cross-link hint */}
        {conceptIds.size > 0 && (
          <div className="p-3 border-t border-navy-800">
            <p className="text-[10px] text-slate-600">
              Concept IDs in <code className="font-mono">code spans</code> are clickable — they jump to the Explorer.
            </p>
          </div>
        )}
      </div>

      {/* Right: rendered markdown */}
      <div className="flex-1 overflow-y-auto" style={{ backgroundColor: '#0d1527' }}>
        {referenceLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 size={20} className="animate-spin text-brand-teal" />
          </div>
        ) : referenceDoc?.name === activeDoc && referenceDoc.content ? (
          <div ref={contentRef} className="max-w-3xl mx-auto px-10 py-10">
            {activeDocMeta && (
              <div className="mb-8 pb-6 border-b border-navy-700">
                <h1 className="text-2xl font-bold text-white tracking-tight">{activeDocMeta.label}</h1>
                <p className="text-sm text-slate-400 mt-2">{activeDocMeta.description}</p>
              </div>
            )}
            {/* 
              prose-invert requires @tailwindcss/typography.
              We also apply explicit color overrides so each element is clearly readable.
            */}
            <div className="prose prose-invert prose-sm max-w-none
              prose-headings:font-semibold prose-headings:tracking-tight
              prose-h1:text-xl prose-h1:text-white prose-h1:mb-4 prose-h1:mt-8
              prose-h2:text-lg prose-h2:text-slate-100 prose-h2:mb-3 prose-h2:mt-7 prose-h2:border-b prose-h2:border-navy-700 prose-h2:pb-2
              prose-h3:text-base prose-h3:text-slate-200 prose-h3:mb-2 prose-h3:mt-5
              prose-h4:text-sm prose-h4:text-slate-300 prose-h4:mb-1.5 prose-h4:mt-4
              prose-p:text-slate-300 prose-p:leading-7 prose-p:mb-4
              prose-li:text-slate-300 prose-li:leading-6
              prose-ul:my-3 prose-ol:my-3
              prose-code:text-[#2dd4bf] prose-code:bg-[#151e32] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[0.82em] prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
              prose-pre:bg-slate-900 prose-pre:border prose-pre:border-navy-700 prose-pre:rounded-lg prose-pre:my-4
              prose-blockquote:border-l-4 prose-blockquote:border-brand-teal prose-blockquote:bg-navy-800/50 prose-blockquote:px-4 prose-blockquote:py-2 prose-blockquote:rounded-r prose-blockquote:text-slate-400 prose-blockquote:not-italic prose-blockquote:my-4
              prose-a:text-brand-teal prose-a:no-underline hover:prose-a:underline prose-a:font-medium
              prose-hr:border-navy-700 prose-hr:my-8
              prose-table:text-sm prose-table:w-full
              prose-thead:bg-navy-800
              prose-th:text-slate-100 prose-th:font-semibold prose-th:px-3 prose-th:py-2 prose-th:text-left
              prose-td:text-slate-300 prose-td:px-3 prose-td:py-2 prose-td:border-t prose-td:border-navy-700
              prose-strong:text-white prose-strong:font-semibold
              prose-em:text-slate-200 prose-em:italic
            ">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={components as Record<string, React.ElementType>}
              >
                {referenceDoc.content}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-slate-600 text-sm">
            Select a document to view
          </div>
        )}
      </div>
    </div>
  );
}
